import { debounce } from 'lodash-es'
import type { ReactNode } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import type {
  CaptchaToken,
  VisibleCaptchaRef,
} from 'src/components/Account/Captcha/Captcha'
import { VisibleTurnstileWrapper } from 'src/components/Account/Captcha/TurnstileWrapper'
import { useCheckEmailVerified } from 'src/features/User/ForceVerifyEmail/useForceVerifyEmail'
import { track } from 'src/utils/tracker'
import { useMyQuota } from '../apollo/quota'
import {
  ClaimIds,
  useClaimClaimMutation,
  useDailyClaimInfo,
} from '../orpc/claim'

export default function useDailyClaim(
  getTurnstileToken?: () => string | null | Promise<string | null>,
) {
  const { data: claimInfo } = useDailyClaimInfo()
  const available = claimInfo?.canClaim ?? false
  const amount = claimInfo?.amount ?? 0
  const refreshAt = claimInfo?.nextClaimableTime ?? null

  const emailVerificationRequiredSince =
    claimInfo?.emailVerificationRequiredSince ?? null
  const checkEmailVerified = useCheckEmailVerified()

  const { refetch: reloadQuota } = useMyQuota()

  const { t } = useTranslation(['user'])

  const { mutateAsync: executeClaim } = useClaimClaimMutation()

  /**
   * Claim daily rewards with optional Cloudflare Turnstile verification
   *
   * @returns `true` if success, `false` if failed
   */
  const claimImpl = useCallback(async () => {
    if (!(await checkEmailVerified())) {
      return false
    }

    track('claim_credit_click', { quest: 'daily' })
    try {
      // Get turnstile token if available
      const turnstileToken = await getTurnstileToken?.()
      const headers: Record<string, string> = {}
      if (turnstileToken) {
        headers['X-Turnstile-Token'] = turnstileToken
      }

      await executeClaim({
        id: ClaimIds.DAILY_CLAIM,
        headers,
      })
      await reloadQuota()
      toast.success(t('user:claim-credit-tasks.successfully-claimed'))
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
      return false
    }
  }, [checkEmailVerified, getTurnstileToken, executeClaim, reloadQuota, t])

  const claim = useMemo(
    () => debounce(claimImpl, 100, { leading: true, trailing: false }),
    [claimImpl],
  )

  return {
    available,
    amount,
    claim,
    refreshAt,
    emailVerificationRequiredSince,
  }
}

/**
 * Hook that combines useDailyClaim with Turnstile verification.
 * Returns a TurnstileWidget component that should be rendered in your component.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { TurnstileWidget, available, amount, claim } = useDailyClaimWithTurnstile()
 *
 *   return (
 *     <>
 *       <TurnstileWidget />
 *       <button onClick={claim}>Claim {amount}</button>
 *     </>
 *   )
 * }
 * ```
 */
export function useDailyClaimWithTurnstile() {
  const turnstileRef = useRef<VisibleCaptchaRef>(null)
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken>()

  const getTurnstileToken = useCallback(() => {
    return captchaToken?.token ?? null
  }, [captchaToken])

  const dailyClaim = useDailyClaim(getTurnstileToken)

  const claimAndReset = useCallback(async () => {
    const result = await dailyClaim.claim()
    turnstileRef.current?.reset?.()
    return result
  }, [dailyClaim])

  const TurnstileWidget = useCallback(
    (): ReactNode =>
      dailyClaim.available ? (
        <VisibleTurnstileWrapper
          ref={turnstileRef}
          setToken={setCaptchaToken}
          action="dailyClaim"
        />
      ) : null,
    [dailyClaim.available],
  )

  // Ready to claim when not available (already claimed) or has token
  const turnstileReady = !dailyClaim.available || Boolean(captchaToken?.token)

  return {
    ...dailyClaim,
    claim: claimAndReset,
    TurnstileWidget,
    turnstileReady,
  }
}
